'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  updateAppointmentTiming,
  getBookingById,
  getPatient,
  getAppointmentRecordings,
  getRecordingStatus,
  getDetailedBooking,
  getAppointmentRecording,
} from '@/client/sdk.gen';
import {
  BookingResponse,
  AppointmentTimingUpdate,
  PatientRead,
  BookingRecordingResponse,
  BookingRecordingStatusResponse,
  SOAPNoteRead,
  BookingDetailedResponse,
} from '@/client/types.gen';
import {
  Loader2,
  Play,
  StopCircle,
  Mic,
  MicOff,
  ArrowLeft,
  User,
  Clock,
  AlertCircle,
  ClipboardList,
  Pill,
  CheckCircle,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { settings } from '@/lib/settings';
import { SELECTED_ORG_KEY } from '@/constants';
import { Badge } from '@/components/ui/badge';

// Extended BookingResponse with additional properties returned by the API
interface ExtendedBookingResponse extends BookingResponse {
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  location_details?:
    | {
        type?: string;
        details?: {
          link?: string;
          [key: string]: unknown;
        };
      }
    | string
    | null;
  event_type?: {
    name: string;
    duration: number;
    [key: string]: unknown;
  };
  notes?: string | null;
}

// Common hook for fetching booking data
const useBookingData = (bookingId: string | null) => {
  const [booking, setBooking] = useState<ExtendedBookingResponse | null>(null);
  const [patient, setPatient] = useState<PatientRead | null>(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) {
      setError('Missing booking ID. Please go back to appointments list.');
      setIsLoading(false);
      return;
    }

    const fetchBookingDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getBookingById({
          path: { booking_id: bookingId },
        });

        if (response.data) {
          const bookingData = response.data as ExtendedBookingResponse;
          setBooking(bookingData);

          if (bookingData.patient_id) {
            setIsLoadingPatient(true);
            try {
              const patientResponse = await getPatient({
                path: { patient_id: bookingData.patient_id },
              });
              if (patientResponse.data) {
                setPatient(patientResponse.data);
              }
            } catch (error) {
              console.error('Error fetching patient data:', error);
            } finally {
              setIsLoadingPatient(false);
            }
          }
        } else if (response.error) {
          setError('Failed to load appointment details. API error occurred.');
        } else {
          setError('Failed to load appointment details. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching appointment details:', error);
        setError('Failed to load appointment details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  return { booking, patient, isLoadingPatient, isLoading, error, setBooking };
};

export function AppointmentEncounterPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const navigate = useNavigate();
  const { toast } = useToast();

  const { booking, patient, isLoadingPatient, isLoading, setBooking } =
    useBookingData(bookingId);
  const [localError, setLocalError] = useState<string | null>(null);

  // State variables for encounter
  const [isStarting, setIsStarting] = useState(false);
  const [isAppointmentStarted, setIsAppointmentStarted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordings, setRecordings] = useState<BookingRecordingResponse[]>([]);

  // Refs for recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (booking?.actual_start_time) {
      setIsAppointmentStarted(true);
    }
  }, [booking]);

  // Check for existing recordings
  useEffect(() => {
    if (bookingId && isAppointmentStarted) {
      checkForRecordings();
    }
  }, [bookingId, isAppointmentStarted]);

  const checkForRecordings = async () => {
    if (!bookingId) return;

    try {
      const response = await getAppointmentRecordings({
        path: { booking_id: bookingId },
      });

      if (response.data && response.data.length > 0) {
        setHasRecording(true);
        setRecordings(response.data);
      }
    } catch (error) {
      console.error('Error checking for recordings:', error);
    }
  };

  const startAppointment = async () => {
    if (!bookingId) return;

    setIsStarting(true);
    setLocalError(null);

    try {
      const now = new Date();
      const timingUpdate: AppointmentTimingUpdate = {
        actual_start_time: now.toISOString(),
      };

      const response = await updateAppointmentTiming({
        path: { booking_id: bookingId },
        body: timingUpdate,
      });

      if (response.data) {
        const bookingData = response.data as ExtendedBookingResponse;
        setBooking(bookingData);
        setIsAppointmentStarted(true);
        toast({
          title: 'Visit Started',
          description:
            'The patient visit has been started. You can now begin recording.',
        });
      } else if (response.error) {
        setLocalError('Failed to start the visit: API Error');
      } else {
        setLocalError('Failed to start the visit. Please try again.');
      }
    } catch (error) {
      console.error('Error starting appointment:', error);
      setLocalError(
        `Failed to start the visit: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setIsStarting(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      toast({
        title: 'Recording Started',
        description:
          'Your visit conversation is now being recorded for AI documentation.',
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: 'Recording Error',
        description:
          'Failed to start recording. Please check microphone permissions.',
        variant: 'destructive',
      });
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());

      mediaRecorderRef.current.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/webm',
          });
          await uploadRecording(audioBlob);
        }
      };

      setIsRecording(false);
    }
  };

  const uploadRecording = async (audioBlob: Blob) => {
    if (!bookingId) {
      console.error('Cannot upload recording: Missing booking ID');
      toast({
        title: 'Upload Error',
        description: 'Missing booking ID. Cannot upload recording.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const fileName = `appointment_recording_${Date.now()}.webm`;
      const file = new File([audioBlob], fileName, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('file', file);

      const url = `${settings.apiDomain}/v1/bookings/appointments/${bookingId}/recordings/upload`;
      const orgId = localStorage.getItem(SELECTED_ORG_KEY);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'X-Organisation-ID': orgId || '',
        },
      });

      if (response.ok) {
        toast({
          title: 'Recording Uploaded',
          description:
            'Your recording has been uploaded and is being processed by AI.',
        });
        await checkForRecordings();
      } else {
        let errorDetail = 'Unknown error occurred.';
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || JSON.stringify(errorData);
        } catch {
          errorDetail = `Server responded with status ${response.status}`;
        }

        toast({
          title: 'Upload Failed',
          description: `Failed to upload recording: ${errorDetail}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error during direct upload fetch:', error);
      toast({
        title: 'Upload Error',
        description: 'An unexpected error occurred during upload.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const endAppointment = async () => {
    if (!bookingId || !booking) return;

    if (isRecording) {
      await stopRecording();
    }

    await checkForRecordings();

    try {
      const now = new Date();
      const timingUpdate: AppointmentTimingUpdate = {
        actual_end_time: now.toISOString(),
      };

      const response = await updateAppointmentTiming({
        path: { booking_id: bookingId },
        body: timingUpdate,
      });

      if (response.data) {
        const bookingData = response.data as ExtendedBookingResponse;
        setBooking(bookingData);

        toast({
          title: 'Visit Ended',
          description: 'The visit has been completed successfully.',
        });

        // Navigate to summary page
        navigate(`/dashboard/appointments/summary?booking_id=${bookingId}`);
      } else {
        setLocalError('Failed to end the visit. Please try again.');
      }
    } catch (error) {
      console.error('Error ending appointment:', error);
      setLocalError('Failed to end the visit. Please try again.');
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const goBack = () => {
    navigate('/dashboard/appointments');
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <Alert>
        <AlertDescription>No appointment found.</AlertDescription>
      </Alert>
    );
  }

  const patientName =
    booking.invitees && booking.invitees.length > 0
      ? booking.invitees[0].invitee_info.name
      : 'Unknown Patient';

  return (
    <div className="container mx-auto py-4 flex flex-col h-screen">
      <div className="flex items-center gap-2 pb-2">
        <Button variant="outline" size="sm" onClick={goBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Appointments
        </Button>
      </div>

      <div className="pb-2">
        <h1 className="text-2xl font-bold tracking-tight">Patient Visit</h1>
        {localError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{localError}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="grid grid-rows-[auto_1fr_auto] h-[calc(100vh-180px)] gap-4">
        {/* Patient Information Card */}
        <Card className="overflow-hidden">
          <CardHeader className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Patient Information
                </CardTitle>
                <CardDescription>
                  Basic details about the patient
                </CardDescription>
              </div>
              {isLoadingPatient && (
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="text-xs text-muted-foreground">
                    Loading patient data...
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="py-2">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold text-primary">
                  {patientName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-medium">
                    {patient?.name || patientName}
                  </h3>
                  {patient && (
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(patient.date_of_birth), 'MMMM d, yyyy')}{' '}
                      •
                      <Badge variant="outline" className="ml-1 mr-1">
                        {patient.gender === 'male'
                          ? 'Male'
                          : patient.gender === 'female'
                          ? 'Female'
                          : 'Other'}
                      </Badge>{' '}
                      •{patient.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  {booking.event_type?.name || 'General Visit'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(booking.start_time), 'MMM d, yyyy h:mm a')} •
                  Duration: {booking.event_type?.duration || 30} minutes
                </p>
              </div>
            </div>

            {patient && (
              <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    Allergies
                  </h3>
                  {patient.allergies && patient.allergies.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {patient.allergies.map((allergy, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="bg-destructive/10 text-destructive border-destructive/20"
                        >
                          {allergy.allergen}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No known allergies
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium flex items-center gap-2">
                    <Pill className="h-4 w-4 text-muted-foreground" />
                    Medications (Last 7 Days)
                  </h3>
                  {patient?.medications && patient.medications.length > 0 ? (
                    <div className="mt-1 space-y-1 max-h-60 overflow-y-auto pr-1">
                      {patient.medications
                        .filter((m) => {
                          // Filter medications from the last 7 days
                          if (!m.start_date) return false;
                          const startDate = new Date(m.start_date);
                          const oneWeekAgo = new Date();
                          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                          return startDate >= oneWeekAgo;
                        })
                        .map((med, i) => (
                          <div
                            key={i}
                            className="text-xs bg-primary/5 border border-primary/10 rounded-md p-1.5"
                          >
                            <div className="font-medium text-primary">
                              {med.name}
                            </div>
                            <div className="grid grid-cols-2 gap-x-1 mt-0.5">
                              <div>
                                <span className="text-muted-foreground">
                                  Dosage:
                                </span>{' '}
                                {med.dosage || 'N/A'}
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Started:
                                </span>{' '}
                                {format(new Date(med.start_date), 'MMM d')}
                              </div>
                              {med.frequency && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">
                                    Frequency:
                                  </span>{' '}
                                  {med.frequency}
                                </div>
                              )}
                              {med.reason && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">
                                    Reason:
                                  </span>{' '}
                                  {med.reason}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      {patient.medications.filter((m) => {
                        if (!m.start_date) return false;
                        const startDate = new Date(m.start_date);
                        const oneWeekAgo = new Date();
                        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                        return startDate >= oneWeekAgo;
                      }).length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          No medications in the last 7 days
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No active medications
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clinical Documentation Card */}
        {isAppointmentStarted ? (
          <Card className="flex flex-col">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                AI-Assisted Documentation
              </CardTitle>
              <CardDescription>
                {isRecording
                  ? 'Recording in progress - AI will automatically generate documentation from this audio'
                  : hasRecording
                  ? 'Recording complete - Ready to end the visit'
                  : 'Start recording to enable AI-assisted documentation'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-grow flex flex-col items-center justify-center py-6">
              {isRecording ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Mic className="h-20 w-20 text-red-500 animate-pulse" />
                  <p className="mt-4 text-xl font-semibold text-red-500">
                    Recording...
                  </p>
                  <p className="text-lg text-muted-foreground">
                    {formatRecordingTime(recordingTime)}
                  </p>
                </div>
              ) : recordings.length > 0 ? (
                <div className="text-center text-muted-foreground max-w-md mx-auto">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <h3 className="text-lg font-semibold mb-2 text-foreground">
                    Recording Complete
                  </h3>
                  <p className="text-sm">
                    The audio from your visit has been successfully captured.
                  </p>
                  <p className="text-xs mt-2 px-4">
                    Click the{' '}
                    <strong className="text-primary font-medium">
                      End Visit
                    </strong>{' '}
                    button below to process the recording and generate
                    AI-assisted documentation.
                  </p>
                </div>
              ) : (
                <div className="text-center text-muted-foreground max-w-md mx-auto">
                  <ClipboardList className="h-12 w-12 mx-auto mb-3 text-primary/70" />
                  <h3 className="text-lg font-semibold mb-2 text-foreground">
                    AI Documentation Ready
                  </h3>
                  <p className="text-sm">
                    Click the{' '}
                    <strong className="text-primary font-medium">
                      'Start Recording'
                    </strong>{' '}
                    button below to begin capturing the visit.
                  </p>
                  <p className="text-xs mt-2 px-4">
                    Our AI will transcribe and structure the conversation into
                    clinical notes automatically after the recording is
                    uploaded.
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="border-t py-6">
              <div className="w-full">
                {(() => {
                  // State 1: Initial visit state, no recording started or existing.
                  if (!isRecording && !hasRecording) {
                    return (
                      <div className="grid grid-cols-1 gap-4">
                        <Button
                          onClick={startRecording}
                          className="h-16 text-lg" // w-full is implicit in grid-cols-1
                          size="lg"
                          disabled={isUploading}
                        >
                          <Mic className="h-6 w-6 mr-2" />
                          Start Recording
                        </Button>
                      </div>
                    );
                  }
                  // State 2: Currently recording (could be the first recording or a subsequent one).
                  // Show Stop Recording and End Visit side-by-side.
                  else if (isRecording) {
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Button
                          onClick={stopRecording}
                          variant="destructive"
                          className="h-16 text-lg"
                          size="lg"
                          disabled={isUploading}
                        >
                          <MicOff className="h-6 w-6 mr-2" />
                          Stop Recording ({formatRecordingTime(recordingTime)})
                        </Button>
                        <Button
                          onClick={endAppointment}
                          variant="outline" // Paired with Stop, so outline
                          className="h-16 text-lg"
                          size="lg"
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <StopCircle className="h-6 w-6 mr-2" />
                              End Visit
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  }
                  // State 3: Has one or more recordings, not currently recording.
                  // Show only End Visit, full width.
                  else if (hasRecording && !isRecording) {
                    return (
                      <div className="grid grid-cols-1 gap-4">
                        <Button
                          onClick={endAppointment}
                          variant="default" // Standalone, so default. Implicitly primary.
                          className="h-16 text-lg"
                          size="lg"
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <StopCircle className="h-6 w-6 mr-2" />
                              End Visit
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  }
                  return null; // Should not be reached if isAppointmentStarted is true and logic is complete
                })()}
              </div>
            </CardFooter>
          </Card>
        ) : (
          <Card className="flex flex-col">
            <CardHeader className="py-4">
              <CardTitle>Start Patient Visit</CardTitle>
              <CardDescription>
                Begin documenting this patient encounter with AI assistance
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-grow flex items-center justify-center py-6">
              <div className="text-center max-w-md space-y-6">
                <div className="h-32 w-32 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Play className="h-16 w-16 text-primary" />
                </div>
                <h3 className="text-2xl font-medium">Ready to Begin</h3>
                <p className="text-muted-foreground">
                  Click the button below to start the visit and enable
                  AI-assisted documentation. Our AI will automatically generate
                  clinical notes from your conversation.
                </p>
              </div>
            </CardContent>
            <CardFooter className="border-t py-6">
              <Button
                onClick={startAppointment}
                disabled={isStarting}
                className="w-full h-16 text-lg"
                size="lg"
              >
                {isStarting ? (
                  <>
                    <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                    Starting Visit...
                  </>
                ) : (
                  <>
                    <Play className="h-6 w-6 mr-2" />
                    Start Visit
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-card p-6 rounded-lg shadow-lg border">
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <h3 className="text-xl font-bold mb-2">Uploading Recording</h3>
                <p className="text-muted-foreground mb-4">
                  Please wait while we upload your recording for AI processing
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AppointmentSummaryPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const navigate = useNavigate();
  const { toast } = useToast();

  const { booking, patient, isLoading } = useBookingData(bookingId);
  const [localError, setLocalError] = useState<string | null>(null);

  // State variables for summary
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [recordings, setRecordings] = useState<BookingRecordingResponse[]>([]);
  const [soapNotes, setSoapNotes] = useState<SOAPNoteRead[] | null>(null);
  const [recordingStatus, setRecordingStatus] =
    useState<BookingRecordingStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [detailedBooking, setDetailedBooking] =
    useState<BookingDetailedResponse | null>(null);
  const [recordingId, setRecordingId] = useState<string | null>(null);

  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);

  const getStatusHelpText = (status: string | undefined): string => {
    switch (status) {
      case 'PROCESSING':
        return 'Processing your recording, please wait...';
      case 'TRANSCRIBING':
        return 'Transcribing audio to text...';
      case 'EXTRACTING':
        return 'Extracting key clinical information...';
      case 'FINALISING':
        return 'Finalising the report and generating notes...';
      case 'COMPLETED':
        return 'Processing complete. Report is ready.';
      case 'FAILED':
        return 'Processing failed. Please try again or contact support.';
      case 'TRANSCRIPTION_FAILED':
        return 'Transcription failed. Please check the audio quality or try again.';
      case 'EXTRACTION_FAILED':
        return 'Information extraction failed. The AI could not process the transcript.';
      case 'STORAGE_FAILED':
        return 'Failed to save the recording. Please try again.';
      default:
        return 'Checking status...';
    }
  };

  const getProgressPercentage = (status: string | undefined): number => {
    switch (status) {
      case 'PROCESSING':
        return 20;
      case 'TRANSCRIBING':
        return 40;
      case 'EXTRACTING':
        return 70;
      case 'FINALISING':
        return 90;
      case 'COMPLETED':
        return 100;
      case 'FAILED':
      case 'TRANSCRIPTION_FAILED':
      case 'EXTRACTION_FAILED':
      case 'STORAGE_FAILED':
        return 100;
      default:
        return 10;
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchData();
    }
  }, [bookingId]);

  const fetchData = async () => {
    setIsProcessing(true);
    await Promise.all([
      checkForRecordings(),
      fetchDetailedBooking(bookingId as string),
    ]);
    setIsProcessing(false);
  };

  const startPollingRecordingStatus = (
    bookingId: string,
    recordingId: string
  ) => {
    if (!bookingId || !recordingId) return;

    setIsPolling(true);
    setRecordingId(recordingId);

    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
    }

    statusPollingRef.current = setInterval(async () => {
      try {
        const response = await getRecordingStatus({
          path: {
            booking_id: bookingId,
            recording_id: recordingId,
          },
        });

        if (response.data) {
          setRecordingStatus(response.data);
          setProcessingProgress(
            response.data.progress ||
              getProgressPercentage(response.data.status)
          );

          if (
            response.data.status === 'COMPLETED' ||
            response.data.status === 'FAILED' ||
            response.data.status === 'TRANSCRIPTION_FAILED' ||
            response.data.status === 'EXTRACTION_FAILED' ||
            response.data.status === 'STORAGE_FAILED'
          ) {
            stopPollingRecordingStatus();
            fetchDetailedBooking(bookingId);
          }
        }
      } catch (error) {
        console.error('Error polling recording status:', error);
      }
    }, 3000);
  };

  const stopPollingRecordingStatus = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
    setIsPolling(false);
  };

  const fetchDetailedBooking = async (bookingId: string) => {
    if (!bookingId) return;

    try {
      const response = await getDetailedBooking({
        path: { booking_id: bookingId },
      });

      if (response.data) {
        setDetailedBooking(response.data);
        setSoapNotes(response.data.soap_notes || null);
      }
    } catch (error) {
      console.error('Error fetching detailed booking:', error);
      setLocalError('Failed to fetch booking details');
    }
  };

  const fetchRecordingDetails = async (
    bookingId: string,
    recordingId: string
  ) => {
    if (!bookingId || !recordingId) return;

    try {
      const response = await getAppointmentRecording({
        path: {
          booking_id: bookingId,
          recording_id: recordingId,
        },
      });

      if (response.data) {
        // Start polling status if not completed
        if (
          response.data.status !== 'COMPLETED' &&
          response.data.status !== 'FAILED' &&
          response.data.status !== 'TRANSCRIPTION_FAILED' &&
          response.data.status !== 'EXTRACTION_FAILED' &&
          response.data.status !== 'STORAGE_FAILED'
        ) {
          startPollingRecordingStatus(bookingId, recordingId);
        }
      }
    } catch (error) {
      console.error('Error fetching recording details:', error);
    }
  };

  const checkForRecordings = async () => {
    if (!bookingId) return;

    try {
      const response = await getAppointmentRecordings({
        path: { booking_id: bookingId },
      });

      if (response.data && response.data.length > 0) {
        setRecordings(response.data);

        const latestRecording = response.data[response.data.length - 1];
        setRecordingId(latestRecording.id);
        await fetchRecordingDetails(bookingId, latestRecording.id);
      }
    } catch (error) {
      console.error('Error checking for recordings:', error);
      setLocalError('Failed to load recordings');
    }
  };

  const handleFinaliseReport = () => {
    toast({
      title: 'Report Finalised',
      description:
        "The report has been finalised and saved to the patient's record.",
    });

    if (patient) {
      setTimeout(() => {
        navigate(`/dashboard/patients/patient/${patient.id}`);
      }, 1500);
    }
  };

  const goBack = () => {
    navigate('/dashboard/appointments');
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <Alert>
        <AlertDescription>No appointment found.</AlertDescription>
      </Alert>
    );
  }

  const patientName =
    patient?.name ||
    (booking.invitees && booking.invitees.length > 0
      ? booking.invitees[0].invitee_info.name
      : 'Unknown Patient');

  return (
    <div className="container mx-auto py-4 flex flex-col h-screen">
      <div className="flex items-center justify-between pb-2">
        <Button variant="outline" size="sm" onClick={goBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Appointments
        </Button>

        {recordingStatus && (
          <Badge
            className={`
              ${
                recordingStatus.status === 'COMPLETED'
                  ? 'bg-green-500/10 text-green-600 border-green-500/20'
                  : ''
              }
              ${
                [
                  'FAILED',
                  'TRANSCRIPTION_FAILED',
                  'EXTRACTION_FAILED',
                  'STORAGE_FAILED',
                ].includes(recordingStatus.status)
                  ? 'bg-destructive/10 text-destructive border-destructive/20'
                  : ''
              }
              ${
                ![
                  'COMPLETED',
                  'FAILED',
                  'TRANSCRIPTION_FAILED',
                  'EXTRACTION_FAILED',
                  'STORAGE_FAILED',
                ].includes(recordingStatus.status)
                  ? 'bg-primary/10 text-primary border-primary/20'
                  : ''
              }
            `}
            variant="outline"
          >
            {recordingStatus.status === 'COMPLETED' ? (
              <>
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                Processing Complete
              </>
            ) : [
                'FAILED',
                'TRANSCRIPTION_FAILED',
                'EXTRACTION_FAILED',
                'STORAGE_FAILED',
              ].includes(recordingStatus.status) ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
                Processing Failed
              </>
            ) : (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Processing
              </>
            )}
          </Badge>
        )}
      </div>

      <div className="pb-4">
        <h1 className="text-2xl font-bold tracking-tight">Visit Summary</h1>
        <p className="text-muted-foreground">
          Review and finalise the documentation for this patient visit
        </p>
        {localError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{localError}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="flex flex-col h-[calc(100vh-180px)] gap-6 overflow-auto">
        {isProcessing ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <p className="text-xl font-medium">
              Processing visit documentation...
            </p>
            <div className="w-full max-w-sm mx-auto mt-4 bg-secondary rounded-full h-3">
              <div
                className="bg-primary h-3 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-muted-foreground">
              {recordingStatus
                ? getStatusHelpText(recordingStatus.status)
                : 'Loading...'}
            </p>
          </div>
        ) : (
          <>
            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Patient Information */}
              <div className="space-y-6">
                <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                  <CardHeader className="pb-2 bg-card/50">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <User className="h-5 w-5 text-primary" />
                      Patient Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {patient ? (
                      <div className="space-y-5">
                        <div className="flex items-center space-x-4">
                          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold text-primary">
                            {patientName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="text-lg font-medium">
                              {patientName}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {format(
                                new Date(patient.date_of_birth),
                                'MMMM d, yyyy'
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3 pt-2 border-t">
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span className="text-sm text-muted-foreground">
                              Gender:
                            </span>
                            <span className="text-sm font-medium capitalize">
                              {patient.gender || 'Not specified'}
                            </span>
                          </div>

                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span className="text-sm text-muted-foreground">
                              Phone:
                            </span>
                            <span className="text-sm font-medium">
                              {patient.phone || 'Not available'}
                            </span>
                          </div>

                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span className="text-sm text-muted-foreground">
                              Email:
                            </span>
                            <span className="text-sm font-medium">
                              {patient.email || 'Not available'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3 pt-2 border-t">
                          <h4 className="text-sm font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-muted-foreground" />
                            Allergies
                          </h4>
                          {patient.allergies && patient.allergies.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {patient.allergies.map((allergy, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="bg-destructive/10 text-destructive border-destructive/20"
                                >
                                  {allergy.allergen}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No known allergies
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-6 text-center">
                        <User className="h-12 w-12 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          Patient information not available
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                  <CardHeader className="pb-2 bg-card/50">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      Visit Details
                    </CardTitle>
                    <CardDescription>
                      Appointment information and AI-extracted details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-4">
                      {/* Basic appointment details */}
                      <div className="space-y-3">
                        {detailedBooking?.title && (
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span className="text-sm text-muted-foreground">
                              Title:
                            </span>
                            <span className="text-sm font-medium">
                              {detailedBooking.title}
                            </span>
                          </div>
                        )}

                        {detailedBooking?.description && (
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span className="text-sm text-muted-foreground">
                              Description:
                            </span>
                            <span className="text-sm">
                              {detailedBooking.description}
                            </span>
                          </div>
                        )}

                        <div className="grid grid-cols-[100px_1fr] gap-2">
                          <span className="text-sm text-muted-foreground">
                            Visit Type:
                          </span>
                          <span className="text-sm font-medium">
                            {booking.event_type?.name || 'General Visit'}
                          </span>
                        </div>

                        <div className="grid grid-cols-[100px_1fr] gap-2">
                          <span className="text-sm text-muted-foreground">
                            Status:
                          </span>
                          <span className="text-sm font-medium">
                            <Badge
                              variant="outline"
                              className={`
                                ${
                                  detailedBooking?.status === 'COMPLETED'
                                    ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                    : ''
                                }
                                ${
                                  detailedBooking?.status === 'SCHEDULED'
                                    ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                                    : ''
                                }
                                ${
                                  detailedBooking?.status === 'CANCELLED'
                                    ? 'bg-destructive/10 text-destructive border-destructive/20'
                                    : ''
                                }
                                ${
                                  detailedBooking?.status === 'IN_PROGRESS'
                                    ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                    : ''
                                }
                              `}
                            >
                              {detailedBooking?.status ?? booking.status}
                            </Badge>
                          </span>
                        </div>

                        <div className="grid grid-cols-[100px_1fr] gap-2">
                          <span className="text-sm text-muted-foreground">
                            Date:
                          </span>
                          <span className="text-sm font-medium">
                            {format(
                              new Date(booking.start_time),
                              'MMMM d, yyyy'
                            )}
                          </span>
                        </div>

                        <div className="grid grid-cols-[100px_1fr] gap-2">
                          <span className="text-sm text-muted-foreground">
                            Scheduled:
                          </span>
                          <div className="text-sm font-medium">
                            <div>
                              {format(new Date(booking.start_time), 'h:mm a')}
                              {' - '}
                              {format(new Date(booking.end_time), 'h:mm a')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              (Scheduled duration:{' '}
                              {booking.event_type?.duration || 30} minutes)
                            </div>
                          </div>
                        </div>

                        {booking.actual_start_time &&
                          booking.actual_end_time && (
                            <>
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Actual:
                                </span>
                                <div className="text-sm font-medium">
                                  <div>
                                    {format(
                                      new Date(booking.actual_start_time),
                                      'h:mm a'
                                    )}
                                    {' - '}
                                    {format(
                                      new Date(booking.actual_end_time),
                                      'h:mm a'
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    (Actual duration:{' '}
                                    {Math.round(
                                      (new Date(
                                        booking.actual_end_time
                                      ).getTime() -
                                        new Date(
                                          booking.actual_start_time
                                        ).getTime()) /
                                        60000
                                    )}{' '}
                                    minutes)
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                      </div>

                      {/* AI-extracted visit information */}
                      {(() => {
                        const visitData =
                          detailedBooking?.visit ||
                          detailedBooking?.agentic_structured_output?.visit;

                        if (!visitData) return null;

                        return (
                          <div className="pt-4 mt-4 border-t space-y-3">
                            <h3 className="text-sm font-medium">
                              AI-Extracted Visit Information
                            </h3>

                            {visitData.chief_complaint && (
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Chief Complaint:
                                </span>
                                <span className="text-sm font-medium">
                                  {visitData.chief_complaint}
                                </span>
                              </div>
                            )}

                            {visitData.diagnosis && (
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Diagnosis:
                                </span>
                                <span className="text-sm font-medium">
                                  {Array.isArray(visitData.diagnosis)
                                    ? visitData.diagnosis.join(', ')
                                    : visitData.diagnosis || 'N/A'}
                                </span>
                              </div>
                            )}

                            {visitData.treatment_plan && (
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Treatment Plan:
                                </span>
                                <span className="text-sm">
                                  {visitData.treatment_plan}
                                </span>
                              </div>
                            )}

                            {visitData.follow_up_instructions && (
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Follow-up:
                                </span>
                                <span className="text-sm">
                                  {visitData.follow_up_instructions}
                                </span>
                              </div>
                            )}

                            {visitData.visit_notes && (
                              <div className="mt-3 space-y-2">
                                <span className="text-sm font-medium">
                                  Visit Notes:
                                </span>
                                <div className="text-sm bg-muted/30 p-3 rounded-md">
                                  {visitData.visit_notes}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Recordings, Medications and SOAP Notes */}
              <div className="space-y-6">
                {/* Recordings */}
                <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                  <CardHeader className="pb-2 bg-card/50">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Mic className="h-5 w-5 text-primary" />
                      Recordings{' '}
                      {recordings.length > 0 && `(${recordings.length})`}
                    </CardTitle>
                    <CardDescription>
                      Audio recording from this visit
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {recordings.length > 0 ? (
                      <div className="space-y-4">
                        {recordings.map((recording, index) => (
                          <div
                            key={recording.id}
                            className={`p-4 rounded-lg border ${
                              recordingId === recording.id
                                ? 'border-primary bg-primary/5'
                                : 'border-primary/20 bg-primary/5'
                            } flex justify-between items-center`}
                          >
                            <div className="flex items-center gap-3">
                              <Mic className="h-5 w-5 text-primary" />
                              <div>
                                <p className="font-medium">
                                  Recording {index + 1}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {format(
                                    new Date(recording.created_at),
                                    'MMM d, yyyy • h:mm a'
                                  )}
                                  {recording.recording_duration_seconds &&
                                    ` • ${Math.floor(
                                      recording.recording_duration_seconds / 60
                                    )}:${(
                                      recording.recording_duration_seconds % 60
                                    )
                                      .toString()
                                      .padStart(2, '0')}`}
                                </p>
                              </div>
                            </div>

                            {isPolling &&
                              ![
                                'COMPLETED',
                                'FAILED',
                                'TRANSCRIPTION_FAILED',
                                'EXTRACTION_FAILED',
                                'STORAGE_FAILED',
                              ].includes(recording.status || '') && (
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                  <span className="text-xs">Processing</span>
                                </div>
                              )}
                          </div>
                        ))}

                        {recordingStatus &&
                          ![
                            'COMPLETED',
                            'FAILED',
                            'TRANSCRIPTION_FAILED',
                            'EXTRACTION_FAILED',
                            'STORAGE_FAILED',
                          ].includes(recordingStatus.status) && (
                            <div className="w-full bg-secondary rounded-full h-2 mt-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${processingProgress}%` }}
                              ></div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Mic className="h-12 w-12 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          No recordings found for this visit
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Medications */}
                <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                  <CardHeader className="pb-2 bg-card/50">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Pill className="h-5 w-5 text-primary" />
                      Current Medications
                    </CardTitle>
                    <CardDescription>
                      Prescribed during this visit
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {detailedBooking?.medications?.medications &&
                    detailedBooking.medications.medications.length > 0 ? (
                      <div className="grid gap-3">
                        {detailedBooking.medications.medications.map(
                          (med, i) => (
                            <div
                              key={i}
                              className="p-3 border rounded-md bg-card/50 hover:bg-card/75 transition-colors"
                            >
                              <div className="font-medium text-primary flex items-center gap-2">
                                <Pill className="h-4 w-4" />
                                {med.name}
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                                {med.dosage && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Dosage:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {med.dosage}
                                    </span>
                                  </div>
                                )}
                                {med.frequency && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Frequency:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {med.frequency}
                                    </span>
                                  </div>
                                )}
                                {med.start_date && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Started:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {format(
                                        new Date(med.start_date),
                                        'MMM d, yyyy'
                                      )}
                                    </span>
                                  </div>
                                )}
                                {med.end_date && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Ended:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {format(
                                        new Date(med.end_date),
                                        'MMM d, yyyy'
                                      )}
                                    </span>
                                  </div>
                                )}
                                {med.reason && (
                                  <div className="col-span-2 mt-1">
                                    <span className="text-muted-foreground">
                                      Reason:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {med.reason}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-6 text-center">
                        <Pill className="h-12 w-12 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          No medications prescribed during this visit
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* SOAP Notes */}
                {soapNotes && soapNotes.length > 0 ? (
                  <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                    <CardHeader className="pb-2 bg-card/50">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        SOAP Notes
                      </CardTitle>
                      <CardDescription>
                        AI-generated clinical documentation
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-5">
                        {soapNotes[0].subjective && (
                          <div className="space-y-2">
                            <h3 className="text-base font-medium flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-blue-500/10 text-blue-600 border-blue-500/20"
                              >
                                Subjective
                              </Badge>
                            </h3>
                            <div className="text-sm rounded-md border p-3 bg-card/50">
                              {soapNotes[0].subjective}
                            </div>
                          </div>
                        )}

                        {soapNotes[0].objective && (
                          <div className="space-y-2">
                            <h3 className="text-base font-medium flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              >
                                Objective
                              </Badge>
                            </h3>
                            <div className="text-sm rounded-md border p-3 bg-card/50">
                              {soapNotes[0].objective}
                            </div>
                          </div>
                        )}

                        {soapNotes[0].assessment && (
                          <div className="space-y-2">
                            <h3 className="text-base font-medium flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-amber-500/10 text-amber-600 border-amber-500/20"
                              >
                                Assessment
                              </Badge>
                            </h3>
                            <div className="text-sm rounded-md border p-3 bg-card/50">
                              {soapNotes[0].assessment}
                            </div>
                          </div>
                        )}

                        {soapNotes[0].plan && (
                          <div className="space-y-2">
                            <h3 className="text-base font-medium flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-purple-500/10 text-purple-600 border-purple-500/20"
                              >
                                Plan
                              </Badge>
                            </h3>
                            <div className="text-sm rounded-md border p-3 bg-card/50">
                              {soapNotes[0].plan}
                            </div>
                          </div>
                        )}

                        {detailedBooking?.agentic_structured_output?.soap_note
                          ?.additional_notes && (
                          <div className="space-y-2">
                            <h3 className="text-base font-medium flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20"
                              >
                                Additional Notes
                              </Badge>
                            </h3>
                            <div className="text-sm rounded-md border p-3 bg-card/50">
                              {
                                detailedBooking.agentic_structured_output
                                  .soap_note.additional_notes
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ) : recordingStatus &&
                  ![
                    'COMPLETED',
                    'FAILED',
                    'TRANSCRIPTION_FAILED',
                    'EXTRACTION_FAILED',
                    'STORAGE_FAILED',
                  ].includes(recordingStatus.status) ? (
                  <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                    <CardHeader className="pb-2 bg-card/50">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        SOAP Notes
                      </CardTitle>
                      <CardDescription>
                        AI-generated clinical documentation
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex flex-col items-center justify-center p-8 text-center">
                        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          Processing Documentation
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-md">
                          Our AI is analyzing the recording to generate
                          comprehensive SOAP notes.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="shadow-sm hover:shadow transition-shadow duration-200">
                    <CardHeader className="pb-2 bg-card/50">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        SOAP Notes
                      </CardTitle>
                      <CardDescription>
                        AI-generated clinical documentation
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <ClipboardList className="h-12 w-12 text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">
                          No clinical documentation available yet
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-2 pb-4 sticky bottom-0 bg-background border-t mt-auto">
              <Button variant="outline" onClick={goBack}>
                Back to Appointments
              </Button>
              <Button
                onClick={handleFinaliseReport}
                disabled={!soapNotes || soapNotes.length === 0}
                className="min-w-32"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Finalise Report
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
